import { canReopen, isLegacyExpired } from "../packages";
import { ANTECIPACOES_COLUMN_IDS, AntecipacoesColumnId } from "../preferences/types";
import type { SlaBadgeValue } from "../packages";
import type { ExpiredMotive, PackageRegistration, SlaPause } from "../registration-seal";

export type AdvanceRow = {
  id: number;
  doctor_name: string;
  doctor_crm?: string;
  doctor_crm_uf?: string;
  company_name?: string;
  company_active?: boolean;
  request_date?: string | null;
  payment_date?: string | null;
  vacancies_qty?: number;
  total_gross_value?: number | null;
  total_pay_value?: number | null;
  total_fee_value?: number | null;
  status: string;
  /** Bloco do BE-1g; ausente em BE antigo ou pacote fechado. */
  sla?: SlaBadgeValue | null;
  /** ISO BRT, só em `expired` (BE-1g). */
  expired_at?: string | null;
  /** BE-1h (só na lista): origem do →expired (`legacy_cleanup` | `job` | null); ausente em BE antigo. */
  expired_source?: string | null;
  channel?: string | null;
  /** Conversão do ME (BE-C2, chave registrationSeal): agregado do selo; null = pacote sem plantão do ME; ausente = chave off. */
  registration?: PackageRegistration | null;
  /** BE-C2, chave slaRegistrationPause: SLA da empresa pausado por registro pendente no ME. */
  slaPause?: SlaPause | null;
  /** BE-C2: motivo do expirado (sistema × empresa). */
  motive?: ExpiredMotive | null;
};

export const FIXED_COLUMNS = [
  { id: "id", label: "ID" },
  { id: "doctor_name", label: "Nome" },
  { id: "status", label: "Status" },
  // Fixa: antecipacoesColumns é validado pelo BE nas preferências; uma coluna opcional mudaria esse contrato.
  { id: "sla", label: "SLA" },
  { id: "actions", label: "Ações" },
];

export const OPTIONAL_COLUMN_LABELS: Record<AntecipacoesColumnId, string> = {
  crm: "CRM",
  company_name: "Empresa",
  request_date: "Data da solicitação",
  payment_date: "Data de Pagamento",
  vacancies_qty: "Qtde. Plantões",
  total_gross_value: "Valor Bruto",
  total_pay_value: "Valor a ser pago",
  total_fee_value: "Valor total da taxa",
};

export function buildColumnVisibilityModel(visible: AntecipacoesColumnId[]): Record<AntecipacoesColumnId, boolean> {
  return Object.fromEntries(ANTECIPACOES_COLUMN_IDS.map((id) => [id, visible.includes(id)])) as Record<AntecipacoesColumnId, boolean>;
}

export function toggleColumn(visible: AntecipacoesColumnId[], id: AntecipacoesColumnId): AntecipacoesColumnId[] {
  const set = new Set(visible);
  if (set.has(id)) set.delete(id);
  else set.add(id);
  return ANTECIPACOES_COLUMN_IDS.filter((c) => set.has(c));
}

export type ActionRule = { visible: boolean; disabled: boolean; hint: string | null };

const CLOSED = ["done", "canceled", "merged", "expired"];

// Mesmas regras de TableAntecipe/index.js:137-200 (antes eram 4 colunas de botões).
export function rowActionRules(row: AdvanceRow, canWrite: boolean, now: Date = new Date()) {
  const closed = CLOSED.includes(row.status);
  const noDate = !row.payment_date;
  const needsDate: ActionRule = { visible: canWrite, disabled: closed || noDate, hint: noDate && !closed ? "Defina a data de pagamento" : null };
  return {
    changeStatus: needsDate,
    dutys: { visible: true, disabled: false, hint: null } as ActionRule,
    contract: needsDate,
    // Cancelar um expirado é permitido pelo BE (exceção do §7.2 do spec).
    cancel: { visible: canWrite, disabled: (closed && row.status !== "expired") || row.status === "waiting_payment_doctor", hint: null } as ActionRule,
    reopen: reopenRule(row, canWrite, now),
  };
}

// F1b: reabrir só o expirado, até 30 d de `expired_at` (§7.2). A chave packageExpiration fica com quem monta o menu.
function reopenRule(row: AdvanceRow, canWrite: boolean, now: Date): ActionRule {
  // Expirado pela limpeza do legado não reabre (BE-1h, 409 REOPEN_LEGACY): esconde o item.
  if (!canWrite || row.status !== "expired" || isLegacyExpired(row)) return { visible: false, disabled: true, hint: null };
  const { allowed, hint } = canReopen(row, now);
  return { visible: true, disabled: !allowed, hint };
}
